// frontend/src/components/ui/LoadingSpinner.tsx
import React from 'react';
import { cn } from '@/utils';

export interface LoadingSpinnerProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  color?: 'primary' | 'white' | 'gray' | 'current';
  thickness?: 'thin' | 'normal' | 'thick';
  label?: string;
  fullScreen?: boolean;
  overlay?: boolean;
  inline?: boolean;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  className,
  color = 'primary',
  thickness = 'normal',
  label,
  fullScreen = false,
  overlay = false,
  inline = false
}) => {
  const sizeClasses = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12'
  };

  const colorClasses = {
    primary: 'text-blue-600 dark:text-blue-500',
    white: 'text-white',
    gray: 'text-gray-400 dark:text-gray-500',
    current: 'text-current'
  };

  const thicknessClasses = {
    thin: 'stroke-1',
    normal: 'stroke-2',
    thick: 'stroke-[3]'
  };

  const labelSizeClasses = {
    xs: 'text-xs',
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
    xl: 'text-lg'
  };

  const spinner = (
    <div className={cn(
      'flex items-center justify-center',
      inline ? 'inline-flex' : '',
      !inline && !fullScreen && !overlay ? 'flex-col space-y-2' : ''
    )}>
      <svg
        className={cn(
          'animate-spin',
          sizeClasses[size],
          colorClasses[color],
          className
        )}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        role="status"
        aria-label={label || 'Loading'}
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      
      {label && !inline && (
        <p className={cn(
          'text-gray-600 dark:text-gray-400 font-medium',
          labelSizeClasses[size]
        )}>
          {label}
        </p>
      )}
      
      {label && inline && (
        <span className={cn(
          'ml-2 text-gray-600 dark:text-gray-400',
          labelSizeClasses[size]
        )}>
          {label}
        </span>
      )}
    </div>
  );

  // Full screen loading
  if (fullScreen) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <svg
            className={cn(
              'animate-spin mx-auto mb-4',
              sizeClasses.xl,
              colorClasses[color]
            )}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            role="status"
            aria-label={label || 'Loading'}
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          {label && (
            <p className="text-lg text-gray-600 dark:text-gray-400 font-medium">
              {label}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Overlay loading
  if (overlay) {
    return (
      <div className="absolute inset-0 z-40 flex items-center justify-center bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm">
        <div className="text-center p-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <svg
            className={cn(
              'animate-spin mx-auto mb-2',
              sizeClasses[size === 'xs' || size === 'sm' ? 'lg' : 'xl'],
              colorClasses[color]
            )}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            role="status"
            aria-label={label || 'Loading'}
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          {label && (
            <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
              {label}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Inline or regular loading
  return spinner;
};

// Convenience components for common use cases
export const FullScreenLoader: React.FC<Omit<LoadingSpinnerProps, 'fullScreen'>> = (props) => (
  <LoadingSpinner {...props} fullScreen />
);

export const OverlayLoader: React.FC<Omit<LoadingSpinnerProps, 'overlay'>> = (props) => (
  <LoadingSpinner {...props} overlay />
);

export const InlineLoader: React.FC<Omit<LoadingSpinnerProps, 'inline'>> = (props) => (
  <LoadingSpinner {...props} inline />
);